/**
 * The way of one tile's frame from „waiting for its turn“ to „shows its page“
 * (I-3641). Every frame loads a whole Connect, and any of its files can fail on
 * the way (a server restarting, a new deploy, the browser out of request slots).
 * A frame that failed never recovers by itself: the failure is noticed here, and
 * it gets a fresh frame. A tile that keeps failing says so and offers to try
 * again, instead of leaving an empty area behind.
 */
import { ref } from "vue";

export type TileFramePhase =
  | "waiting"
  | "starting"
  | "booted"
  | "shown"
  | "failed";

/** The frame's code has not run within this time: it did not load. */
export const TILE_BOOT_TIMEOUT_MS = 30_000;
/** The document is there, its code may still be about to run. */
export const TILE_BOOT_GRACE_MS = 3_000;
/** Still nothing to see after this: say so, and offer to start again. */
export const TILE_SLOW_AFTER_MS = 12_000;
/** The code runs but its first page never came: it gets a fresh frame. */
export const TILE_SHOW_TIMEOUT_MS = 45_000;
/** Starts in a row before the tile shows that it failed. */
export const TILE_MAX_STARTS = 4;
/** The pause before each further start: what failed is rarely back at once. */
const RESTART_PAUSES_MS = [1_000, 3_000, 6_000];

/**
 * What can be seen of the frame from outside: `unreachable` is a browser error
 * page (another origin), `not-yet` a document whose code has not run.
 */
export type TileFrameProbe = "unreachable" | "running" | "not-yet";

export interface TileFrameOptions {
  /** Put a fresh frame in place. */
  start: () => void;
  probe: () => TileFrameProbe;
  /** The frame's code runs: it can be talked to. */
  onBooted: () => void;
  /** Its turn in the start queue is over. */
  release: () => void;
}

export function createTileFrameLoader(options: TileFrameOptions) {
  const phase = ref<TileFramePhase>("waiting");
  /** Loading for long: the user may want to start it again. */
  const slow = ref(false);
  let starts = 0;
  /** It failed and waits for its pause: what the old frame still says changes nothing. */
  let restarting = false;
  /** This frame's `onBooted` went out. */
  let told = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let slowTimer: ReturnType<typeof setTimeout> | undefined;

  function arm(ms: number, then: () => void): void {
    clearTimeout(timer);
    timer = setTimeout(then, ms);
  }

  function tell(): void {
    if (told) return;
    told = true;
    options.onBooted();
  }

  function begin(): void {
    restarting = false;
    told = false;
    starts += 1;
    phase.value = "starting";
    options.start();
    arm(TILE_BOOT_TIMEOUT_MS, fail);
    if (!slowTimer && !slow.value)
      slowTimer = setTimeout(() => {
        slowTimer = undefined;
        if (phase.value !== "shown") slow.value = true;
      }, TILE_SLOW_AFTER_MS);
  }

  function fail(): void {
    if (restarting) return;
    clearTimeout(timer);
    options.release();
    if (starts >= TILE_MAX_STARTS) {
      clearTimeout(slowTimer);
      slowTimer = undefined;
      slow.value = false;
      phase.value = "failed";
      return;
    }
    phase.value = "starting";
    restarting = true;
    arm(RESTART_PAUSES_MS[Math.max(0, starts - 1)] ?? 6_000, begin);
  }

  function booted(): void {
    if (phase.value !== "starting" || restarting) return;
    phase.value = "booted";
    arm(TILE_SHOW_TIMEOUT_MS, fail);
    tell();
  }

  function shown(): void {
    restarting = false;
    clearTimeout(timer);
    clearTimeout(slowTimer);
    slowTimer = undefined;
    slow.value = false;
    starts = 0;
    phase.value = "shown";
    options.release();
    // Its code runs, also when `booted` came in the pause before a restart.
    tell();
  }

  /** The frame's `load`: fires for an error page too, and when a frame reloaded itself. */
  function documentLoaded(): void {
    if (restarting) return;
    const seen = options.probe();
    if (seen === "unreachable") {
      fail();
    } else if (seen === "running") {
      booted();
    } else {
      arm(TILE_BOOT_GRACE_MS, () => {
        if (options.probe() === "running") booted();
        else fail();
      });
    }
  }

  return {
    phase,
    slow,
    /** Its turn in the start queue. */
    begin,
    documentLoaded,
    booted,
    shown,
    /** The frame says it cannot show its page. */
    failed: fail,
    /** The user asks for a fresh frame: a clean slate. */
    restart(): void {
      restarting = false;
      clearTimeout(timer);
      clearTimeout(slowTimer);
      slowTimer = undefined;
      slow.value = false;
      starts = 0;
      begin();
    },
    dispose(): void {
      clearTimeout(timer);
      clearTimeout(slowTimer);
      slowTimer = undefined;
    },
  };
}
